import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 mb-8 fade-up">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="font-display text-4xl text-[#0b1f3a]" data-testid="page-title">{title}</h1>
        {description && <div className="text-sm text-slate-500 mt-2 max-w-2xl">{description}</div>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

export function Stat({ label, value, hint, testid }) {
  return (
    <div className="border border-slate-200 rounded-md px-5 py-4 bg-white" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="tabular text-[28px] text-[#0b1f3a] mt-2 leading-none">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}

const LABELS = {
  draft: ["Brouillon", "bg-slate-100 text-slate-700"], active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  paused: ["En pause", "bg-amber-50 text-amber-800 border-amber-200"], stopped: ["Arrêtée", "bg-red-50 text-red-700 border-red-200"],
  completed: ["Terminée", "bg-slate-100 text-slate-700"], queued: ["En file", "bg-slate-100 text-slate-700"],
  sending: ["Envoi…", "bg-blue-50 text-blue-700"], sent: ["Envoyé", "bg-blue-50 text-blue-800 border-blue-200"],
  replied: ["A répondu", "bg-emerald-50 text-emerald-700 border-emerald-200"], bounced: ["Rebond", "bg-red-50 text-red-700 border-red-200"],
  unsubscribed: ["Désinscrit", "bg-red-50 text-red-700"], excluded: ["Exclu", "bg-slate-100 text-slate-600"],
  duplicate: ["Doublon", "bg-slate-100 text-slate-600"], failed: ["Échec", "bg-red-50 text-red-700"],
  needs_review: ["À vérifier", "bg-amber-50 text-amber-800 border-amber-200"],
  verified: ["Vérifié", "bg-emerald-50 text-emerald-700 border-emerald-200"], verified_manual: ["Vérifié (manuel)", "bg-emerald-50 text-emerald-700"],
  risky: ["Risqué", "bg-amber-50 text-amber-800"], unverified: ["Non vérifié", "bg-slate-100 text-slate-600"], invalid: ["Invalide", "bg-red-50 text-red-700"],
  pending: ["En attente", "bg-slate-100 text-slate-600"], in_progress: ["En cours", "bg-blue-50 text-blue-700"],
  found: ["Contact trouvé", "bg-emerald-50 text-emerald-700"], found_unverified: ["Trouvé · non fiable", "bg-amber-50 text-amber-800"],
  not_found: ["Aucun contact", "bg-slate-100 text-slate-600"], error: ["Erreur", "bg-red-50 text-red-700"],
  running: ["En cours", "bg-blue-50 text-blue-700"], cancelled: ["Annulé", "bg-slate-100 text-slate-600"],
  interested: ["Intéressé", "bg-emerald-50 text-emerald-700"], not_interested: ["Pas intéressé", "bg-slate-100 text-slate-700"],
  info_request: ["Demande d'infos", "bg-blue-50 text-blue-700"], meeting_request: ["Demande de RDV", "bg-emerald-100 text-emerald-800"],
  out_of_office: ["Absence / auto", "bg-slate-100 text-slate-600"], unsubscribe: ["Désinscription", "bg-red-50 text-red-700"],
  bounce: ["Rebond", "bg-red-50 text-red-700"], other: ["Autre", "bg-slate-100 text-slate-600"],
};

export const statusLabel = (s) => (LABELS[s] || [s || "—"])[0];

export function StatusBadge({ status, testid }) {
  const [label, cls] = LABELS[status] || [status || "—", "bg-slate-100 text-slate-600"];
  return <Badge variant="outline" data-testid={testid} className={`font-medium text-[11px] rounded-sm border-transparent ${cls}`}>{label}</Badge>;
}

export function Empty({ title, children }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-md py-14 text-center">
      <div className="font-display text-xl text-[#0b1f3a]">{title}</div>
      {children && <div className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{children}</div>}
    </div>
  );
}

export function Pager({ page, total, pageSize = 50, onChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between text-xs text-slate-500 mt-4" data-testid="pager">
      <span className="tabular">{total.toLocaleString("fr-CH")} résultats · page {page}/{pages}</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pager-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pager-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function Section({ title, description, children, actions }) {
  return (
    <section className="border border-slate-200 rounded-md bg-white">
      <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-[#0b1f3a]">{title}</h2>
          {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}
